import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { compradorTrpc } from '../../lib/compradorTrpc';
import { AppShellComprador } from '../../components/comprador/AppShellComprador';
import { mesCurto, mesLongo, useCompradorSessao, sairParaLogin } from './util';

interface Etapa {
  id: string;
  ordem: number;
  titulo: string;
  descricao: string | null;
  status: 'prevista' | 'em_andamento' | 'concluida';
  dataPrevista: string | null;
  dataConcluida: string | null;
}

interface Obra {
  temObra: boolean;
  etapas: Etapa[];
  entregaPrevista: string | null;
  notificarObra?: boolean;
}

/**
 * Minha obra (design aprovado): rastreamento vertical estilo entrega —
 * concluídas com check, a atual destacada com descrição, futuras neutras.
 * Estado vazio acolhedor quando a incorporadora ainda não publicou etapas.
 * Preferência "avisar por e-mail a cada avanço" mora aqui.
 */
export function ObraComprador() {
  const slug = useCompradorSessao();
  const [, navigate] = useLocation();
  const [corMarca, setCorMarca] = useState<string | null>(null);
  const [orgNome, setOrgNome] = useState('');
  const [obra, setObra] = useState<Obra | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [notificar, setNotificar] = useState(true);
  const [salvandoPref, setSalvandoPref] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let vivo = true;
    Promise.all([
      compradorTrpc.auth.me.query({ orgSlug: slug }),
      compradorTrpc.app.obra.query({ orgSlug: slug }),
    ])
      .then(([m, o]) => {
        if (!vivo) return;
        setCorMarca(m.corMarca);
        setOrgNome(m.orgNome);
        setObra(o as Obra);
        setNotificar((o as Obra).notificarObra ?? true);
      })
      .catch(() => sairParaLogin(slug, navigate))
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [slug, navigate]);

  async function alternarNotificar() {
    if (!slug) return;
    const novo = !notificar;
    setNotificar(novo);
    setSalvandoPref(true);
    try {
      await compradorTrpc.app.setNotificarObra.mutate({ orgSlug: slug, valor: novo });
    } catch {
      setNotificar(!novo); // desfaz se falhar
    } finally {
      setSalvandoPref(false);
    }
  }

  const etapas = obra?.etapas ?? [];

  return (
    <AppShellComprador corMarca={corMarca} orgNome={orgNome}>
      <div data-testid="obra-comprador">
        <h1 className="font-serif text-[26px] font-medium tracking-tight text-ink">Minha obra</h1>

        {carregando ? (
          <p className="py-16 text-center text-sm text-ink-3">Carregando…</p>
        ) : !obra?.temObra ? (
          /* ── Estado vazio ── */
          <div className="py-10 text-center" data-testid="obra-vazia">
            <div
              className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl"
              style={{ background: 'var(--surface-2)', color: 'var(--ink-4)' }}
              aria-hidden="true"
            >
              <svg viewBox="0 0 24 24" width="34" height="34" fill="none">
                <path d="M5 20V9.5L12 4l7 5.5V20" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 20v-5h6v5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="mt-5 font-serif text-[20px] font-medium text-ink">
              O cronograma da sua obra chega em breve
            </h2>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-ink-3">
              Assim que a {orgNome} publicar as etapas do seu empreendimento, você acompanha cada avanço por
              aqui, semana a semana.
            </p>
            {obra?.entregaPrevista && (
              <div className="card mx-auto mt-6 flex max-w-sm items-center gap-3 p-4 text-left">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                  style={{ background: 'var(--brand-soft)', color: 'var(--brand)' }}
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                    <rect x="4" y="5" width="16" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M4 9.5h16M8.5 3.5v3M15.5 3.5v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </span>
                <div>
                  <div className="text-sm font-semibold text-ink">Previsão de entrega</div>
                  <div className="text-xs text-ink-3">{mesLongo(obra.entregaPrevista)}</div>
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={alternarNotificar}
              disabled={salvandoPref}
              className="mt-6 rounded-xl px-6 py-3 text-[14px] font-semibold text-white disabled:opacity-60"
              style={{ background: 'var(--brand)' }}
              data-testid="obra-avisar"
            >
              {notificar ? 'Vamos avisar você por e-mail' : 'Avisar quando começar'}
            </button>
          </div>
        ) : (
          /* ── Rastreamento ── */
          <>
            {obra.entregaPrevista && (
              <section
                className="mt-4 rounded-3xl p-5 text-white"
                style={{ background: 'linear-gradient(135deg, var(--brand), var(--brand-deep))' }}
                data-testid="obra-hero"
              >
                <div className="text-[13px] text-white/85">Entrega prevista</div>
                <div className="mt-1 font-serif text-[30px] font-medium leading-none tracking-tight">
                  {mesLongo(obra.entregaPrevista)}
                </div>
              </section>
            )}

            <ol className="mt-6" data-testid="obra-etapas">
              {etapas.map((e, i) => {
                const ultima = i === etapas.length - 1;
                const atual = e.status === 'em_andamento';
                const concluida = e.status === 'concluida';
                return (
                  <li key={e.id} className="relative flex gap-4 pb-7" data-testid={`etapa-${e.status}`}>
                    {/* trilho vertical */}
                    {!ultima && (
                      <span
                        className="absolute left-[13px] top-8 h-full w-0.5"
                        style={{ background: concluida ? 'var(--brand)' : 'var(--line-2)' }}
                        aria-hidden="true"
                      />
                    )}
                    {/* marcador */}
                    <span
                      className="relative z-10 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                      style={
                        concluida
                          ? { background: 'var(--brand)', color: 'white' }
                          : atual
                            ? { background: 'white', border: '2.5px solid var(--brand)' }
                            : { background: 'white', border: '2px solid var(--line-2)' }
                      }
                      aria-hidden="true"
                    >
                      {concluida ? (
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none">
                          <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : atual ? (
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: 'var(--brand)' }} />
                      ) : null}
                    </span>
                    {/* conteúdo */}
                    {atual ? (
                      <div className="card -mt-1 flex-1 p-4" style={{ borderColor: 'var(--brand)' }}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[15px] font-semibold text-ink">{e.titulo}</div>
                          <span
                            className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
                            style={{ background: 'var(--brand-soft)', color: 'var(--brand)' }}
                          >
                            Em andamento
                          </span>
                        </div>
                        {e.descricao && <p className="mt-1.5 text-[13px] leading-relaxed text-ink-2">{e.descricao}</p>}
                      </div>
                    ) : (
                      <div className="min-w-0 flex-1 pt-0.5">
                        <div className={`text-[15px] font-semibold ${concluida ? 'text-ink' : 'text-ink-4'}`}>
                          {e.titulo}
                        </div>
                        <div className="mt-0.5 text-xs text-ink-3">
                          {concluida
                            ? `concluída${e.dataConcluida ? ` em ${mesCurto(e.dataConcluida)}` : ''}`
                            : e.dataPrevista
                              ? `previsto ${mesCurto(e.dataPrevista)}`
                              : 'a programar'}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>

            {/* Preferência de aviso */}
            <section className="card mt-2 flex items-center justify-between gap-3 p-4" data-testid="obra-preferencia">
              <div>
                <div className="text-sm font-semibold text-ink">Avisar a cada avanço</div>
                <div className="text-xs text-ink-3">Você recebe um e-mail quando uma etapa é concluída.</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={notificar}
                aria-label="Avisar por e-mail a cada avanço da obra"
                onClick={alternarNotificar}
                disabled={salvandoPref}
                className="relative h-6 w-11 shrink-0 overflow-hidden rounded-full transition-colors disabled:opacity-60"
                style={{ background: notificar ? 'var(--brand)' : 'var(--line-2)' }}
                data-testid="obra-toggle-notificar"
              >
                <span
                  className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform"
                  style={{ transform: notificar ? 'translateX(20px)' : 'translateX(0)' }}
                />
              </button>
            </section>

          </>
        )}
      </div>
    </AppShellComprador>
  );
}
